type AppTag = {
  displayName: string,
};

export const ALL_APP_TAGS = {
  "expert": {
    displayName: "Expert",
  },
  "integration": {
    displayName: "Integrations",
  },
  "auth": {
    displayName: "Authentication",
  },
  "developers": {
    displayName: "For Developers",
  },
  "security": {
    displayName: "Security",
  },
  "operations": {
    displayName: "For Operations",
  },
  "gtm": {
    displayName: "Go-to-market",
  },
  "comms": {
    displayName: "Communications",
  },
  "automation": {
    displayName: "Automation",
  },
  "storage": {
    displayName: "Storage & Databases",
  },
  "various": {
    displayName: "Various",
  },
} as const satisfies Record<string, AppTag>;

type ParentAppId = "authentication" | "analytics";

type App = {
  displayName: string,
  subtitle: string,
  tags: (keyof typeof ALL_APP_TAGS)[],
  stage: "alpha" | "beta" | "stable",
  parentAppId?: ParentAppId,
};

export type AppId = keyof typeof ALL_APPS;

export const ALL_APPS = {
  "authentication": {
    displayName: "Authentication",
    subtitle: "User sign-in and account management",
    tags: ["auth", "security"],
    stage: "stable",
  },
  "fraud-protection": {
    displayName: "Fraud Protection",
    subtitle: "Protect your project from fraud and abuse",
    tags: ["auth", "security"],
    stage: "stable",
    parentAppId: "authentication",
  },
  "onboarding": {
    displayName: "Onboarding",
    subtitle: "Configure user onboarding requirements",
    tags: ["auth"],
    stage: "alpha",
  },
  "teams": {
    displayName: "Teams",
    subtitle: "Team collaboration and management",
    tags: ["auth", "security"],
    stage: "stable",
  },
  "rbac": {
    displayName: "RBAC",
    subtitle: "Role-based access control and permissions",
    tags: ["auth", "security"],
    stage: "stable",
  },
  "api-keys": {
    displayName: "API Keys",
    subtitle: "API key generation and management",
    tags: ["auth", "security", "developers"],
    stage: "stable",
  },
  "payments": {
    displayName: "Payments",
    subtitle: "Payment processing and subscription management",
    tags: ["operations", "gtm"],
    stage: "stable",
  },
  "emails": {
    displayName: "Emails",
    subtitle: "Email template configuration and management",
    tags: ["comms"],
    stage: "stable",
  },
  "support": {
    displayName: "Support",
    subtitle: "Customer conversations, team replies, and internal notes",
    tags: ["comms", "operations"],
    stage: "alpha",
  },
  "email-api": {
    displayName: "Email API",
    subtitle: "Programmatic email sending and delivery",
    tags: ["comms", "developers", "expert"],
    stage: "alpha",
  },
  "data-vault": {
    displayName: "Data Vault",
    subtitle: "Secure storage for sensitive user data",
    tags: ["security", "storage"],
    stage: "beta",
  },
  "webhooks": {
    displayName: "Webhooks",
    subtitle: "Real-time event notifications and integrations",
    tags: ["developers"],
    stage: "stable",
  },
  "tv-mode": {
    displayName: "TV mode",
    subtitle: "Dashboard display for large screens",
    tags: ["various"],
    stage: "alpha",
  },
  "launch-checklist": {
    displayName: "Launch Checklist",
    subtitle: "Pre-launch verification and readiness checks",
    tags: ["various"],
    stage: "stable",
  },
  "catalyst": {
    displayName: "Catalyst",
    subtitle: "Project scaffolding and rapid development",
    tags: ["various"],
    stage: "alpha",
  },
  "neon": {
    displayName: "Neon Integration",
    subtitle: "Serverless Postgres database integration",
    tags: ["integration", "storage"],
    stage: "alpha",
  },
  "convex": {
    displayName: "Convex Integration",
    subtitle: "Real-time backend platform integration",
    tags: ["integration", "storage"],
    stage: "alpha",
  },
  "vercel": {
    displayName: "Vercel Integration",
    subtitle: "Deploy your Hexclave project to Vercel",
    tags: ["integration", "developers"],
    stage: "stable",
  },
  "tanstack-start": {
    displayName: "TanStack Start",
    subtitle: "Use Hexclave in TanStack Start apps",
    tags: ["integration", "developers"],
    stage: "alpha",
  },
  "analytics": {
    displayName: "Analytics",
    subtitle: "View and explore analytics data",
    tags: ["developers", "operations"],
    stage: "stable",
  },
  "clickmaps": {
    displayName: "Clickmaps",
    subtitle: "Visualize where users click across your app",
    tags: ["developers", "operations"],
    stage: "stable",
    parentAppId: "analytics",
  },
  "session-replays": {
    displayName: "Session Replays",
    subtitle: "Watch real user sessions to understand how people use your app",
    tags: ["developers", "operations"],
    stage: "stable",
    parentAppId: "analytics",
  },
  "gtm": {
    displayName: "GTM",
    subtitle: "Internal go-to-market workspace",
    tags: ["gtm", "operations"],
    stage: "alpha",
  },
  "cli-auth": {
    displayName: "CLI Auth",
    subtitle: "Monitor CLI authentication sessions and active tokens",
    tags: ["auth", "developers"],
    stage: "alpha",
  },
  // The app id carries the `-alpha` suffix deliberately: it is the key users
  // write in `apps.installed` and under the top-level config, so the stage is
  // visible in every hexclave.config.ts. The display name stays "Deployments".
  "deployments-alpha": {
    displayName: "Deployments",
    subtitle: "Configure and connect the services that run your app",
    tags: ["developers", "operations"],
    stage: "alpha",
  },
  // Same `-alpha` suffix reasoning as Deployments above. The plain `workflows`
  // id is additionally unusable: a 2025-10-29 config migration strips
  // `apps.installed.workflows` from every override (see migrateConfigOverride),
  // so an app under that id could never stay enabled.
  "workflows-alpha": {
    displayName: "Workflows",
    subtitle: "Durable, code-defined automations that react to events in your project",
    tags: ["automation", "developers"],
    stage: "alpha",
  },
} as const satisfies Record<string, App>;

export function getParentAppId(appId: AppId): AppId | null {
  const app = ALL_APPS[appId];
  return "parentAppId" in app ? app.parentAppId : null;
}
